import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';

import { speedDialButtons } from '../../../../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { ActionButtonModel } from '../../../../models/core/action-button.model';
import { PermissionNodeModel } from '../../../../models/permission-node.model';
import { PermissionModel } from '../../../../models/permission.model';
import { RoleModel } from '../../../../models/role.model';
import { RolesResource } from '../../../../services/resources/roles.resource';

@Component({
    selector: 'sl-permission-settings',
    templateUrl: './permission-settings.component.html',
    styleUrls: ['./permission-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PermissionSettingsComponent implements OnInit {
    groups: PermissionNodeModel[] = [];
    roles: RoleModel[] = [];
    permissionModel: PermissionModel[] = [];
    permissionForm: UntypedFormGroup;
    dataSource: PermissionNodeModel[] = [];
    isLoading = signal(false);

    get permissions(): UntypedFormArray {
        return this.permissionForm.get('permissions') as UntypedFormArray;
    }

    get roleId(): number {
        return this.permissionForm.get('roleId')?.value ?? 0;
    }

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly rolesResource = inject(RolesResource);

    childrenAccessor(node: PermissionNodeModel): PermissionNodeModel[] {
        return node.items;
    }

    ngOnInit(): void {
        this.rolesResource.getAll().subscribe(r => (this.roles = r.value));
        this.permissionForm = this.formBuilder.group({
            roleId: new FormControl(undefined, undefined),
            permissions: new UntypedFormArray([])
        });
    }

    onRoleChange(event: MatSelectChange): void {
        this.isLoading.set(true);
        const selectedRoleId = event.value;

        this.permissionForm.controls['roleId'].setValue(selectedRoleId);
        this.rolesResource.getPermissions(selectedRoleId).subscribe(r => this.initPermissionEditFormForGivenRole(r.value));
    }

    initPermissionEditFormForGivenRole(permissionModel: PermissionModel[]): void {
        this.permissionModel = permissionModel;

        this.permissions.clear();
        this.permissionModel.map(p => this.permissions.push(this.formBuilder.control(p.isGranted)));
        this.groupResults();
        this.setupButtons();
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    hasChild(_: number, node: PermissionNodeModel): boolean {
        return node.items.length > 0;
    }

    getFormControlName(node: PermissionNodeModel): number {
        return this.permissionModel.findIndex(p => p.name === node.name);
    }

    private setupButtons(): void {
        const buttons = [
            new ActionButtonModel({
                icon: 'save',
                clickFunction: () => this.savePermissionSettings(),
                color: 'primary',
                caption: 'General.Save'
            })
        ];

        speedDialButtons.set(buttons);
    }

    private savePermissionSettings(): void {
        const updatedPermissionModel = this.permissionModel.map((permissionModel, index) => {
            permissionModel.isGranted = this.permissionForm.value.permissions[index];
            return permissionModel;
        });
        this.rolesResource.editPermissions(this.roleId.toString(), updatedPermissionModel).subscribe();
    }

    private groupResults(): void {
        this.groups = [];
        const names = this.permissionModel.map(r => (r.groupTitle ? r.groupTitle : ''));
        const distinctGroupNames = [...new Set(names)].sort();

        distinctGroupNames.forEach(n => this.groups.push(new PermissionNodeModel({ groupTitle: n, name: n })));
        this.permissionModel.forEach(item => {
            const group = this.groups.find(g => g.groupTitle === item.groupTitle) || this.groups.find(g => g.groupTitle === '');
            if (!group) {
                return;
            }
            group.items.push(new PermissionNodeModel(item));
        });

        this.dataSource = [...this.groups];

        this.isLoading.set(false);
    }
}
